<template>
  <div class="einvoice-config-page">
    <KTCodePreview v-bind:title="'Cấu hình hoá đơn điện tử'">
      <template v-slot:toolbar>
        <b-button
          variant="primary"
          size="sm"
          class="font-weight-bolder"
          @click="saveConfig"
          :disabled="isSaving"
        >
          <i class="fas fa-save mr-1"></i>
          Lưu cấu hình
        </b-button>
      </template>

      <template v-slot:preview>
        <b-skeleton-wrapper :loading="isLoading">
          <template #loading>
            <b-skeleton width="40%" class="mb-4"></b-skeleton>
            <b-skeleton-table
              :rows="5"
              :columns="4"
              :table-props="{ bordered: true }"
            ></b-skeleton-table>
          </template>

          <!-- FAST Connection Settings -->
          <b-card no-body class="mb-4">
            <b-card-header class="bg-light">
              <h6 class="mb-0 font-weight-bold">
                <i class="fas fa-plug mr-2"></i>
                Kết nối FAST Business Online
              </h6>
            </b-card-header>
            <b-card-body>
              <b-row>
                <b-col md="6">
                  <b-form-group
                    label="Client Code"
                    label-class="font-weight-bold"
                  >
                    <b-form-input
                      v-model="config.clientCode"
                      size="sm"
                      placeholder="Mã khách hàng FAST"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group
                    label="Proxy Code"
                    label-class="font-weight-bold"
                  >
                    <b-form-input
                      v-model="config.proxyCode"
                      size="sm"
                      placeholder="Mã đại lý thuế"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="6">
                  <b-form-group
                    label="Chế độ ký số (B2B)"
                    label-class="font-weight-bold"
                  >
                    <b-form-select
                      v-model="config.defaultSignMode"
                      size="sm"
                      :options="signModeOptions"
                    ></b-form-select>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group
                    label="Tự động phát hành"
                    label-class="font-weight-bold"
                  >
                    <b-form-checkbox
                      v-model="config.autoIssueEnabled"
                      switch
                    >
                      {{ config.autoIssueEnabled ? 'Bật' : 'Tắt' }}
                    </b-form-checkbox>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card-body>
          </b-card>

          <!-- Branch → FAST Unit mapping -->
          <b-card no-body class="mb-4">
            <b-card-header class="bg-light d-flex justify-content-between align-items-center">
              <h6 class="mb-0 font-weight-bold">
                <i class="fas fa-sitemap mr-2"></i>
                Mapping chi nhánh → FAST unitCode / voucherBook
              </h6>
              <b-button
                variant="outline-primary"
                size="sm"
                @click="addBranchMapping"
              >
                <i class="fas fa-plus mr-1"></i>
                Thêm
              </b-button>
            </b-card-header>
            <b-card-body>
              <b-table
                bordered
                hover
                :items="config.branchMappings"
                :fields="branchMappingFields"
                class="mb-0"
                show-empty
                empty-text="Chưa có mapping nào"
              >
                <template #cell(branchId)="{ item, index }">
                  <vue-multiselect
                    v-model="item.branch"
                    :options="branches"
                    label="name"
                    track-by="id"
                    placeholder="Chọn chi nhánh"
                    :searchable="true"
                    :close-on-select="true"
                    :show-labels="false"
                    class="multiselect-sm"
                  ></vue-multiselect>
                </template>
                <template #cell(unitCode)="{ item }">
                  <b-form-input
                    v-model="item.unitCode"
                    size="sm"
                    placeholder="unitCode FAST"
                  ></b-form-input>
                </template>
                <template #cell(voucherBookRetail)="{ item }">
                  <b-form-input
                    v-model="item.voucherBookRetail"
                    size="sm"
                    placeholder="VoucherBook POS"
                  ></b-form-input>
                </template>
                <template #cell(voucherBookWholesale)="{ item }">
                  <b-form-input
                    v-model="item.voucherBookWholesale"
                    size="sm"
                    placeholder="VoucherBook B2B"
                  ></b-form-input>
                </template>
                <template #cell(actions)="{ index }">
                  <b-button
                    variant="outline-danger"
                    size="sm"
                    @click="removeBranchMapping(index)"
                    v-b-tooltip.hover
                    title="Xoá"
                  >
                    <i class="fas fa-trash-alt"></i>
                  </b-button>
                </template>
              </b-table>
            </b-card-body>
          </b-card>

          <!-- Invoice Date Rules -->
          <b-card no-body class="mb-4">
            <b-card-header class="bg-light">
              <h6 class="mb-0 font-weight-bold">
                <i class="fas fa-calendar-alt mr-2"></i>
                Quy tắc ngày hoá đơn
              </h6>
            </b-card-header>
            <b-card-body>
              <b-row>
                <b-col md="6">
                  <b-form-group
                    label="Ngày HĐ bán lẻ"
                    label-class="font-weight-bold"
                    description="Ngày hoá đơn lấy theo ngày nào"
                  >
                    <b-form-select
                      v-model="config.retailInvoiceDateRule"
                      size="sm"
                      :options="invoiceDateRuleOptions"
                    ></b-form-select>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group
                    label="Ngày HĐ bán buôn"
                    label-class="font-weight-bold"
                  >
                    <b-form-select
                      v-model="config.wholesaleInvoiceDateRule"
                      size="sm"
                      :options="invoiceDateRuleOptions"
                    ></b-form-select>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="6">
                  <b-form-group
                    label="Email bắt buộc (POS)"
                    label-class="font-weight-bold"
                  >
                    <b-form-checkbox
                      v-model="config.retailEmailRequired"
                      switch
                    >
                      {{ config.retailEmailRequired ? 'Bắt buộc' : 'Không bắt buộc' }}
                    </b-form-checkbox>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card-body>
          </b-card>
        </b-skeleton-wrapper>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import VueMultiselect from 'vue-multiselect';
import ApiService from '@/core/services/api.service';
import commonMixin from '@/view/mixins/api-common.mixins';
import { getToastInstance } from '@/utils/toastHelper';

const { makeToastSuccess, makeToastFailure } = getToastInstance();

export default {
  name: 'EInvoiceConfig',
  components: { KTCodePreview, VueMultiselect },
  mixins: [commonMixin],
  data() {
    return {
      isLoading: false,
      isSaving: false,
      branches: [],
      config: {
        clientCode: '',
        proxyCode: '',
        defaultSignMode: 'HSM',
        autoIssueEnabled: false,
        retailInvoiceDateRule: 'SALE_DATE',
        wholesaleInvoiceDateRule: 'SALE_DATE',
        retailEmailRequired: true,
        branchMappings: [],
      },
    };
  },
  computed: {
    signModeOptions() {
      return [
        { value: 'HSM', text: 'HSM (Chữ ký số từ xa - khuyến nghị)' },
        { value: 'USB', text: 'USB Token (Chữ ký số USB)' },
      ];
    },
    invoiceDateRuleOptions() {
      return [
        { value: 'SALE_DATE', text: 'Ngày bán hàng' },
        { value: 'ISSUE_DATE', text: 'Ngày phát hành HĐ' },
        { value: 'CURRENT_DATE', text: 'Ngày hiện tại' },
      ];
    },
    branchMappingFields() {
      return [
        { key: 'branchId', label: 'Chi nhánh', thStyle: { minWidth: '200px' } },
        { key: 'unitCode', label: 'Unit Code', thStyle: { width: '150px' } },
        { key: 'voucherBookRetail', label: 'VoucherBook POS', thStyle: { width: '150px' } },
        { key: 'voucherBookWholesale', label: 'VoucherBook B2B', thStyle: { width: '150px' } },
        { key: 'actions', label: '', thStyle: { width: '60px' }, tdClass: 'text-center' },
      ];
    },
  },
  async created() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Hoá đơn điện tử', route: '/einvoices' },
      { title: 'Cấu hình' },
    ]);
    this.isLoading = true;
    try {
      const [branchResult, configResult] = await Promise.all([
        this.fetchStoreByUser(),
        ApiService.get('v1/einvoice/config'),
      ]);
      this.branches = branchResult.stores || [];
      if (configResult?.data?.data) {
        this.config = {
          ...this.config,
          ...configResult.data.data,
        };
      }
    } catch {
      makeToastFailure('Không thể tải cấu hình');
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    addBranchMapping() {
      this.config.branchMappings.push({
        branch: null,
        unitCode: '',
        voucherBookRetail: '',
        voucherBookWholesale: '',
      });
    },
    removeBranchMapping(index) {
      this.config.branchMappings.splice(index, 1);
    },
    async saveConfig() {
      this.isSaving = true;
      try {
        const payload = {
          ...this.config,
          branchMappings: this.config.branchMappings
            .filter((m) => m.branch && m.unitCode)
            .map((m) => ({
              branchId: m.branch.id,
              unitCode: m.unitCode,
              voucherBookRetail: m.voucherBookRetail,
              voucherBookWholesale: m.voucherBookWholesale,
            })),
        };
        await ApiService.post('v1/einvoice/config', payload);
        makeToastSuccess('Lưu cấu hình thành công');
      } catch {
        makeToastFailure('Lưu cấu hình thất bại');
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.einvoice-config-page {
  .multiselect-sm {
    ::v-deep .multiselect__tags {
      min-height: 31px;
      padding: 2px 40px 0 8px;
      font-size: 0.85rem;
    }
    ::v-deep .multiselect__single {
      font-size: 0.85rem;
    }
  }
}
</style>
